import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { CRMNote } from "@/types";

const HIGHLIGHT_PARAM = "highlightNote";
const OPEN_PARAM = "openNote";

interface UseNoteAroundIdOptions {
  resetKey?: string;
  enabled?: boolean;
  highlightParam?: string;
  openParam?: string;
}

function noteAroundIdFromSearchParams(searchParams: URLSearchParams, highlightParam: string, openParam: string) {
  return searchParams.get(openParam) || searchParams.get(highlightParam) || undefined;
}

export function useNoteAroundId({
  resetKey,
  enabled = true,
  highlightParam = HIGHLIGHT_PARAM,
  openParam = OPEN_PARAM
}: UseNoteAroundIdOptions = {}) {
  const [searchParams] = useSearchParams();
  const [aroundId, setAroundId] = useState<string | undefined>(() =>
    enabled ? noteAroundIdFromSearchParams(searchParams, highlightParam, openParam) : undefined
  );
  const previousResetKey = useRef(resetKey);
  const previousTarget = useRef(noteAroundIdFromSearchParams(searchParams, highlightParam, openParam));

  useEffect(() => {
    if (!enabled) {
      previousTarget.current = undefined;
      setAroundId(undefined);
      return;
    }

    const nextAroundId = noteAroundIdFromSearchParams(searchParams, highlightParam, openParam);

    if (previousResetKey.current !== resetKey) {
      previousResetKey.current = resetKey;
      previousTarget.current = nextAroundId;
      setAroundId(undefined);
      return;
    }

    if (nextAroundId && nextAroundId !== previousTarget.current) {
      setAroundId(nextAroundId);
    }
    previousTarget.current = nextAroundId;
  }, [enabled, highlightParam, openParam, resetKey, searchParams]);

  const clearAroundId = useCallback(() => {
    setAroundId(undefined);
  }, []);

  return { aroundId, clearAroundId };
}

interface UseNoteDeepLinkOptions {
  dataReady: boolean;
  expandParam?: string;
  highlightParam?: string;
  openParam?: string;
  persistCurrentPage?: boolean;
  serverCurrentPage?: number;
  rowIds?: string[];
  onTargetMissing?: () => void;
}

interface UseNoteDeepLinkResult {
  expandRowId: string | null;
  highlightNoteId: string | null;
  openNoteId: string | null;
  tableRef: React.RefObject<HTMLDivElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  getCopyLink: (note: CRMNote) => string;
  setOpenNoteInUrl: (noteId: string | null) => void;
}

/**
 * Manages the notes-table deep-link URL contract:
 *
 * - `expandNotes` and `highlightNote` are one-shot triggers stripped from the
 *   URL on arrival once `dataReady` is true.
 * - `openNote` is a live URL-backed value. It is preserved in the URL while
 *   the side sheet is open so users can copy the browser URL to share the
 *   currently open note. Callers sync it via `setOpenNoteInUrl` on open/close.
 * - `openNote` implies highlight and wins over `highlightNote` when both are
 *   present.
 * - Paginated connected tables pass `persistCurrentPage` so the first aroundId
 *   consume writes `currentPage` from the aroundId response (`highlightNote`,
 *   or the first `openNote` jump). Later fetches while `openNote` remains do
 *   not rewrite it — `CRMNoteConnection.currentPage` is 0 for last/before.
 *   `usePagination.syncCurrentPage` is pager state only.
 *
 * Row-level scroll for highlightNote retries against `containerRef` after consume.
 */
export function useNoteDeepLink({
  dataReady,
  expandParam = "expandNotes",
  highlightParam = HIGHLIGHT_PARAM,
  openParam = OPEN_PARAM,
  persistCurrentPage = false,
  serverCurrentPage,
  rowIds,
  onTargetMissing
}: UseNoteDeepLinkOptions): UseNoteDeepLinkResult {
  const [searchParams] = useSearchParams();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [expandRowId, setExpandRowId] = useState<string | null>(null);
  const [highlightNoteId, setHighlightNoteId] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrolledHighlightIdRef = useRef<string | null>(null);

  const openNote = searchParams.get(openParam);
  const openNoteId = dataReady ? openNote : null;

  const getCopyLink = useCallback(
    (note: CRMNote) => `${window.location.origin}${pathname}?${openParam}=${encodeURIComponent(note.id)}`,
    [pathname, openParam]
  );

  const setOpenNoteInUrl = useCallback(
    (noteId: string | null) => {
      const next = new URLSearchParams(search);
      if (noteId) {
        next.set(openParam, noteId);
      } else {
        next.delete(openParam);
      }
      const nextSearch = next.toString();
      const currentSearch = search.startsWith("?") ? search.slice(1) : search;
      if (nextSearch === currentSearch) return;
      navigate({ pathname, search: nextSearch ? `?${nextSearch}` : "" }, { replace: true });
    },
    [navigate, openParam, pathname, search]
  );

  useEffect(() => {
    const expandValue = searchParams.get(expandParam);
    const highlightNote = searchParams.get(highlightParam);
    const isFirstOpenNoteJump =
      persistCurrentPage &&
      Boolean(openNote) &&
      !searchParams.has("currentPage") &&
      !searchParams.has("after") &&
      !searchParams.has("before");

    if (!expandValue && !highlightNote && !isFirstOpenNoteJump) {
      return;
    }

    if (!dataReady || (persistCurrentPage && serverCurrentPage === undefined)) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.delete(expandParam);
    next.delete(highlightParam);
    if (persistCurrentPage) {
      if (serverCurrentPage && serverCurrentPage > 0) {
        next.set("currentPage", String(serverCurrentPage));
      } else {
        next.delete("currentPage");
      }
    }
    const nextSearch = next.toString();
    if (nextSearch !== searchParams.toString()) {
      navigate({ pathname, search: nextSearch ? `?${nextSearch}` : "" }, { replace: true });
    }

    if (expandValue) setExpandRowId(expandValue);

    if (openNote) {
      setHighlightNoteId(openNote);
    } else if (highlightNote && (!rowIds || rowIds.includes(highlightNote))) {
      setHighlightNoteId(highlightNote);
    } else if (highlightNote) {
      setHighlightNoteId(null);
      onTargetMissing?.();
    }

    if (expandValue && tableRef.current) {
      tableRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest"
      });
    }
  }, [
    dataReady,
    expandParam,
    highlightParam,
    navigate,
    onTargetMissing,
    openNote,
    pathname,
    persistCurrentPage,
    rowIds,
    searchParams,
    serverCurrentPage
  ]);

  useEffect(() => {
    if (openNote && dataReady) setHighlightNoteId(openNote);
  }, [openNote, dataReady]);

  useEffect(() => {
    if (!highlightNoteId) {
      scrolledHighlightIdRef.current = null;
      return;
    }

    if (scrolledHighlightIdRef.current === highlightNoteId) {
      return;
    }

    const root = containerRef.current;
    if (!root) {
      return;
    }

    let cancelled = false;
    let attempt = 0;
    let timeoutId: number | undefined;
    let rafId: number | undefined;

    const tryScroll = () => {
      if (cancelled) {
        return;
      }

      const row = root.querySelector("tr.row-highlight") as HTMLElement | null;
      if (row) {
        scrolledHighlightIdRef.current = highlightNoteId;
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      if (attempt++ >= 30) {
        return;
      }

      rafId = requestAnimationFrame(tryScroll);
    };

    timeoutId = window.setTimeout(tryScroll, 0);

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [highlightNoteId]);

  return { expandRowId, highlightNoteId, openNoteId, tableRef, containerRef, getCopyLink, setOpenNoteInUrl };
}
